import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import {
  Save, Plus, Trash2, Sparkles, CheckCircle2, XCircle, Edit3,
  ChevronDown, ChevronUp, Loader2, AlertTriangle, Info, Shield
} from 'lucide-react';
import axios from 'axios';

import { API_URL } from '../../utils/api';

const CATEGORIES = ['Presentation', 'Response', 'Setting', 'Timing/Scheduling', 'Other'];
const TYPES = [
  { value: 'classroom', label: 'Classroom' },
  { value: 'assessment', label: 'Assessment' },
];
const LOCATIONS = ['General Education', 'Special Education', 'Both', 'Testing Only'];
const CONFIDENCE_STYLE = {
  high: 'bg-green-50 text-green-700 border-green-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-gray-50 text-gray-500 border-gray-200',
};

function mergeContent(existing) {
  const defaults = { classroom: [], assessment: [] };
  if (!existing || typeof existing !== 'object') return defaults;
  if (existing.notes && !existing.classroom && !existing.assessment) return defaults;
  return {
    classroom: Array.isArray(existing.classroom) ? existing.classroom : [],
    assessment: Array.isArray(existing.assessment) ? existing.assessment : [],
  };
}

export default function AccommodationsEditor({ section, caseData, onSave, saving }) {
  const [content, setContent] = useState(() => mergeContent(section?.content));
  const [aiOpen, setAiOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [aiMeta, setAiMeta] = useState(null);
  const [suggestionStates, setSuggestionStates] = useState({});

  useEffect(() => {
    setContent(mergeContent(section?.content));
  }, [section?.section_id]);

  // --- Manual Accommodation Management ---
  const addAccommodation = (type) => {
    setContent(prev => ({
      ...prev,
      [type]: [...prev[type], {
        accommodation: '', category: '', location: '', justification: '', source: 'manual',
      }],
    }));
  };

  const removeAccommodation = (type, idx) => {
    setContent(prev => ({ ...prev, [type]: prev[type].filter((_, i) => i !== idx) }));
  };

  const updateAccommodation = (type, idx, field, value) => {
    setContent(prev => ({
      ...prev,
      [type]: prev[type].map((a, i) => i === idx ? { ...a, [field]: value } : a),
    }));
  };

  // --- AI Suggestion Workflow ---
  const generateSuggestions = async () => {
    setGenerating(true);
    setSuggestions(null);
    setAiMeta(null);
    setSuggestionStates({});
    try {
      const res = await axios.post(
        `${API_URL}/api/iep-met/cases/${caseData.case_id}/sections/accommodations/draft`,
        {}, { withCredentials: true }
      );
      const output = res.data?.output;
      if (output?.suggestions) {
        setSuggestions(output.suggestions);
        setAiMeta({
          analysisId: res.data?.analysis_id,
          dataSources: output.data_sources_used || [],
          notes: output.notes || '',
          gaps: output.gaps_identified || [],
          model: res.data?.model_used,
          inputSnapshot: res.data?.input_snapshot,
        });
        const initial = {};
        output.suggestions.forEach((_, i) => { initial[i] = 'pending'; });
        setSuggestionStates(initial);
      }
    } catch (err) {
      console.error('AI generation failed', err);
      setAiMeta({ error: err.response?.data?.detail || 'AI generation failed. Please try again.' });
    } finally {
      setGenerating(false);
    }
  };

  const acceptSuggestion = (idx) => {
    const s = suggestions[idx];
    const type = s.type === 'assessment' ? 'assessment' : 'classroom';
    setContent(prev => ({
      ...prev,
      [type]: [...prev[type], {
        accommodation: s.accommodation,
        category: s.category || '',
        location: s.location || '',
        justification: s.justification || '',
        source: 'ai_suggested',
        confidence: s.confidence,
        data_basis: s.data_basis,
      }],
    }));
    setSuggestionStates(prev => ({ ...prev, [idx]: 'accepted' }));
  };

  const rejectSuggestion = (idx) => {
    setSuggestionStates(prev => ({ ...prev, [idx]: 'rejected' }));
  };

  const editSuggestion = (idx) => {
    const s = suggestions[idx];
    const type = s.type === 'assessment' ? 'assessment' : 'classroom';
    setContent(prev => ({
      ...prev,
      [type]: [...prev[type], {
        accommodation: s.accommodation,
        category: s.category || '',
        location: s.location || '',
        justification: s.justification || '',
        source: 'ai_edited',
        confidence: s.confidence,
        data_basis: s.data_basis,
      }],
    }));
    setSuggestionStates(prev => ({ ...prev, [idx]: 'edited' }));
  };

  const acceptAll = () => {
    const newContent = { ...content };
    suggestions.forEach((s, i) => {
      if (suggestionStates[i] === 'pending') {
        const type = s.type === 'assessment' ? 'assessment' : 'classroom';
        newContent[type] = [...newContent[type], {
          accommodation: s.accommodation,
          category: s.category || '',
          location: s.location || '',
          justification: s.justification || '',
          source: 'ai_suggested',
          confidence: s.confidence,
          data_basis: s.data_basis,
        }];
      }
    });
    setContent(newContent);
    setSuggestionStates(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(k => { if (next[k] === 'pending') next[k] = 'accepted'; });
      return next;
    });
  };

  const handleSave = () => onSave(content);
  const pendingCount = suggestions ? Object.values(suggestionStates).filter(s => s === 'pending').length : 0;
  const totalAccommodations = content.classroom.length + content.assessment.length;

  return (
    <div className="max-w-4xl space-y-6" data-testid="accommodations-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="accommodations-title">Accommodations</h2>
          <p className="text-sm text-[#595959] mt-1">{section?.description}</p>
          <div className="flex items-center gap-2 mt-2">
            <Badge className="bg-[#3B6A9B]/10 text-[#3B6A9B] text-xs">{totalAccommodations} accommodations</Badge>
            <Badge className="bg-gray-100 text-gray-600 text-xs">{content.classroom.length} classroom</Badge>
            <Badge className="bg-gray-100 text-gray-600 text-xs">{content.assessment.length} assessment</Badge>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl shrink-0" data-testid="accommodations-save-top">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {/* AI Suggestion Panel */}
      <div className="bg-gradient-to-r from-[#3B6A9B]/5 to-[#5A8A5A]/5 border border-[#3B6A9B]/20 rounded-2xl overflow-hidden" data-testid="ai-suggestion-panel">
        <button className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-white/40 transition-colors" onClick={() => setAiOpen(!aiOpen)} data-testid="ai-toggle">
          <div className="flex items-center gap-2.5">
            <Sparkles className="h-4 w-4 text-[#3B6A9B]" />
            <span className="text-sm font-medium text-[#1C1C1C]">AI Accommodation Suggestions</span>
            <Badge className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200">Advisory Only</Badge>
            {suggestions && <Badge className="text-[10px] bg-[#3B6A9B]/10 text-[#3B6A9B]">{suggestions.length} suggestions</Badge>}
          </div>
          {aiOpen ? <ChevronUp className="h-4 w-4 text-[#595959]" /> : <ChevronDown className="h-4 w-4 text-[#595959]" />}
        </button>

        {aiOpen && (
          <div className="px-5 pb-5 border-t border-[#3B6A9B]/10">
            <div className="flex items-center justify-between mt-3 mb-4">
              <p className="text-xs text-[#595959] max-w-lg">
                AI analyzes present levels, evaluation findings, goals, and disability context to suggest individualized accommodations. All suggestions require team review.
              </p>
              <Button onClick={generateSuggestions} disabled={generating}
                className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl text-xs h-8 shrink-0" data-testid="generate-suggestions-btn">
                {generating ? <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" />Analyzing...</>
                  : <><Sparkles className="h-3 w-3 mr-1.5" />{suggestions ? 'Regenerate' : 'Generate Suggestions'}</>}
              </Button>
            </div>

            {/* Error State */}
            {aiMeta?.error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-start gap-2" data-testid="ai-error">
                <AlertTriangle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                <p className="text-xs text-red-700">{aiMeta.error}</p>
              </div>
            )}

            {/* Suggestions */}
            {suggestions && suggestions.length > 0 && (
              <>
                {/* Meta Info */}
                {aiMeta && !aiMeta.error && (
                  <div className="bg-white/60 border border-[#E5E5E5] rounded-xl p-3 mb-4" data-testid="ai-meta">
                    <div className="flex items-start gap-4 text-xs text-[#595959]">
                      <div className="flex-1">
                        {aiMeta.notes && <p className="mb-1"><strong>AI Notes:</strong> {aiMeta.notes}</p>}
                        {aiMeta.dataSources?.length > 0 && <p className="mb-1"><strong>Data used:</strong> {aiMeta.dataSources.join(', ')}</p>}
                        {aiMeta.gaps?.length > 0 && (
                          <p className="text-amber-600"><strong>Data gaps:</strong> {aiMeta.gaps.join('; ')}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-[10px] text-[#595959]">{pendingCount} pending</span>
                        {pendingCount > 0 && (
                          <Button variant="outline" size="sm" className="rounded-lg text-xs h-7 border-green-300 text-green-700 hover:bg-green-50" onClick={acceptAll} data-testid="accept-all-btn">
                            <CheckCircle2 className="h-3 w-3 mr-1" />Accept All Remaining
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Suggestion Cards */}
                <div className="space-y-2" data-testid="suggestion-list">
                  {suggestions.map((s, idx) => {
                    const state = suggestionStates[idx] || 'pending';
                    const confStyle = CONFIDENCE_STYLE[s.confidence] || CONFIDENCE_STYLE.medium;
                    const isPending = state === 'pending';
                    const isAccepted = state === 'accepted';
                    const isRejected = state === 'rejected';
                    const isEdited = state === 'edited';

                    return (
                      <div key={idx}
                        className={`border rounded-xl p-3.5 transition-all ${
                          isAccepted ? 'bg-green-50/50 border-green-200 opacity-75' :
                          isRejected ? 'bg-gray-50 border-gray-200 opacity-50' :
                          isEdited ? 'bg-blue-50/50 border-blue-200 opacity-75' :
                          'bg-white border-[#E5E5E5]'
                        }`}
                        data-testid={`suggestion-${idx}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1.5">
                              <span className="text-sm font-medium text-[#1C1C1C]">{s.accommodation}</span>
                              {s.category && <Badge className="text-[10px] bg-[#3B6A9B]/10 text-[#3B6A9B]">{s.category}</Badge>}
                              <Badge className={`text-[10px] ${s.type === 'assessment' ? 'bg-purple-50 text-purple-700' : 'bg-sky-50 text-sky-700'}`}>{s.type}</Badge>
                              {s.confidence && <Badge className={`text-[10px] border ${confStyle}`}>{s.confidence}</Badge>}
                              {!isPending && (
                                <Badge className={`text-[10px] ${
                                  isAccepted ? 'bg-green-100 text-green-700' : isEdited ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                                }`}>
                                  {isAccepted ? 'Accepted' : isEdited ? 'Accepted (to edit)' : 'Rejected'}
                                </Badge>
                              )}
                            </div>
                            {s.justification && <p className="text-xs text-[#595959] mb-1">{s.justification}</p>}
                            <div className="flex items-center gap-3 text-[10px] text-[#595959]/70">
                              {s.location && <span>Setting: {s.location}</span>}
                              {s.data_basis && <span>Basis: {s.data_basis}</span>}
                            </div>
                          </div>

                          {isPending && (
                            <div className="flex items-center gap-1 shrink-0">
                              <Button variant="ghost" size="sm" onClick={() => acceptSuggestion(idx)}
                                className="h-7 w-7 p-0 text-green-600 hover:bg-green-50 rounded-lg" data-testid={`accept-${idx}`}>
                                <CheckCircle2 className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => editSuggestion(idx)}
                                className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50 rounded-lg" data-testid={`edit-${idx}`}>
                                <Edit3 className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => rejectSuggestion(idx)}
                                className="h-7 w-7 p-0 text-red-400 hover:bg-red-50 rounded-lg" data-testid={`reject-${idx}`}>
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Input Audit */}
                {aiMeta?.inputSnapshot && (
                  <div className="mt-3 text-[10px] text-[#595959]/60 flex items-center gap-2">
                    <Info className="h-3 w-3" />
                    <span>
                      Context: {aiMeta.inputSnapshot.context_type} IEP | Sources: {aiMeta.inputSnapshot.data_sources?.join(', ') || 'none'} | Intensity: {aiMeta.inputSnapshot.ai_intensity}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Classroom Accommodations */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="classroom-accommodations">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959]">
            <Shield className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />Classroom Accommodations
          </h3>
          <Button variant="outline" size="sm" className="rounded-xl text-xs h-7" onClick={() => addAccommodation('classroom')} data-testid="add-classroom-btn">
            <Plus className="h-3 w-3 mr-1" />Add
          </Button>
        </div>
        {content.classroom.length === 0 ? (
          <p className="text-sm text-[#595959] text-center py-4 italic">No classroom accommodations added yet.</p>
        ) : (
          <div className="space-y-3">
            {content.classroom.map((acc, idx) => (
              <AccommodationRow key={idx} acc={acc} idx={idx} type="classroom"
                onUpdate={updateAccommodation} onRemove={removeAccommodation} />
            ))}
          </div>
        )}
      </div>

      {/* Assessment Accommodations */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="assessment-accommodations">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959]">
            <Shield className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />Assessment Accommodations
          </h3>
          <Button variant="outline" size="sm" className="rounded-xl text-xs h-7" onClick={() => addAccommodation('assessment')} data-testid="add-assessment-btn">
            <Plus className="h-3 w-3 mr-1" />Add
          </Button>
        </div>
        {content.assessment.length === 0 ? (
          <p className="text-sm text-[#595959] text-center py-4 italic">No assessment accommodations added yet.</p>
        ) : (
          <div className="space-y-3">
            {content.assessment.map((acc, idx) => (
              <AccommodationRow key={idx} acc={acc} idx={idx} type="assessment"
                onUpdate={updateAccommodation} onRemove={removeAccommodation} />
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl" data-testid="accommodations-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Accommodations'}
        </Button>
      </div>
    </div>
  );
}

function AccommodationRow({ acc, idx, type, onUpdate, onRemove }) {
  const isAI = acc.source === 'ai_suggested' || acc.source === 'ai_edited';

  return (
    <div className={`border rounded-xl p-3 group ${isAI ? 'border-[#3B6A9B]/20 bg-[#3B6A9B]/3' : 'border-[#E5E5E5]'}`}
      data-testid={`${type}-acc-${idx}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <Input value={acc.accommodation} onChange={e => onUpdate(type, idx, 'accommodation', e.target.value)}
              placeholder="Accommodation description" className="rounded-lg border-[#E5E5E5] text-sm h-8 flex-1" />
            {isAI && <Badge className="text-[10px] bg-[#3B6A9B]/10 text-[#3B6A9B] shrink-0">AI</Badge>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Select value={acc.category || undefined} onValueChange={v => onUpdate(type, idx, 'category', v)}>
              <SelectTrigger className="rounded-lg text-xs h-7"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={acc.location || undefined} onValueChange={v => onUpdate(type, idx, 'location', v)}>
              <SelectTrigger className="rounded-lg text-xs h-7"><SelectValue placeholder="Setting" /></SelectTrigger>
              <SelectContent>{LOCATIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={acc.justification || ''} onChange={e => onUpdate(type, idx, 'justification', e.target.value)}
              placeholder="Justification" className="rounded-lg border-[#E5E5E5] text-xs h-7" />
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => onRemove(type, idx)}
          className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition shrink-0 mt-0.5"
          data-testid={`remove-${type}-${idx}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
